import type { PatchConferenceVisitorRegistrationBody } from "../../../../hooks/Remote/Schedule/conference-visitor-registration-patch.api";
import type { PatchVisitOnlyBookingBody } from "../../../../hooks/Remote/Schedule/visit-only-patch-booking.api";
import type { LocationInventory } from "../../../../features/FloorManager/typings/location-inventory";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { buildPatchConferenceVisitorRegistrationPayload } from "../../../../features/Admin/components/functions/buildPatchConferenceVisitorRegistrationPayload.function";
import { buildPatchVisitOnlyBookingPayload } from "../../../../features/Admin/components/functions/buildPatchVisitOnlyBookingPayload.function";
import type {
  VisitorBookingEditDraft,
  VisitorBookingTableRow
} from "../../../../features/Admin/components/visitor-bookings-table.partial";
import { isConferenceVisitorRegistrationRow } from "../../functions/visitor-schedule-field-editable.function";

export type ScheduleVisitorDisplayEntry = {
  firstName?: string | null;
  surname?: string | null;
  email?: string | null;
  company?: string | null;
  contacts?: string | null;
  visitReason?: string | null;
  locationName?: string | null;
  visitorRegistrationId?: number;
};

export type ScheduleVisitorEditFormState = {
  firstName: string;
  surname: string;
  email: string;
  company: string;
  contacts: string;
  visitReason: string;
  locationName: string;
};

type CanEditVisitorScheduleDetailsParams = {
  schedule: BookingScheduleInterface;
  userId: string;
  isDialogEditable: boolean;
  isColleagueScheduleEditable: boolean;
};

export function canEditVisitorScheduleDetails({
  schedule,
  userId,
  isDialogEditable,
  isColleagueScheduleEditable
}: CanEditVisitorScheduleDetailsParams): boolean {
  const isOwner =
    schedule.userBookedFor === userId ||
    (schedule.userBookedBy != null && schedule.userBookedBy === userId);

  return (isDialogEditable && isOwner) || isColleagueScheduleEditable;
}

export function hasVisitorDisplayData(visitor: ScheduleVisitorDisplayEntry): boolean {
  return (
    Boolean(visitor.firstName?.trim()) ||
    Boolean(visitor.surname?.trim()) ||
    Boolean(visitor.email?.trim()) ||
    Boolean(visitor.company?.trim()) ||
    Boolean(visitor.contacts?.trim()) ||
    Boolean(visitor.visitReason?.trim()) ||
    Boolean(visitor.locationName?.trim())
  );
}

export function resolveScheduleVisitors(
  schedule: BookingScheduleInterface
): ScheduleVisitorDisplayEntry[] {
  if ((schedule.visitorBookings?.length ?? 0) > 0) {
    return schedule.visitorBookings ?? [];
  }

  if (schedule.isVisitOnly === true || schedule.external === true) {
    return [
      {
        firstName: undefined,
        surname: undefined,
        email: schedule.externalEmail,
        company: schedule.externalCompany,
        contacts: schedule.externalContacts,
        visitReason: schedule.externalVisitReason,
        locationName: schedule.locationName,
        visitorRegistrationId: schedule.visitorRegistrationId
      }
    ];
  }

  return [];
}

export function visitorDisplayName(visitor: ScheduleVisitorDisplayEntry): string {
  return [visitor.firstName?.trim(), visitor.surname?.trim()].filter(Boolean).join(" ");
}

export function visitorEditFormFromEntry(
  visitor: ScheduleVisitorDisplayEntry,
  schedule: BookingScheduleInterface
): ScheduleVisitorEditFormState {
  const externalName = schedule.externalName?.trim();
  let firstName = visitor.firstName?.trim() ?? "";
  let surname = visitor.surname?.trim() ?? "";

  if (!firstName && !surname && externalName) {
    const parts = externalName.split(/\s+/);
    firstName = parts[0] ?? "";
    surname = parts.slice(1).join(" ");
  }

  return {
    firstName,
    surname,
    email: visitor.email?.trim() ?? schedule.externalEmail?.trim() ?? "",
    company: visitor.company?.trim() ?? schedule.externalCompany?.trim() ?? "",
    contacts: visitor.contacts?.trim() ?? schedule.externalContacts?.trim() ?? "",
    visitReason: visitor.visitReason?.trim() ?? schedule.externalVisitReason?.trim() ?? "",
    locationName: visitor.locationName?.trim() ?? schedule.locationName?.trim() ?? ""
  };
}

export function formStateToEditDraft(form: ScheduleVisitorEditFormState): VisitorBookingEditDraft {
  const externalName = [form.firstName.trim(), form.surname.trim()].filter(Boolean).join(" ");

  return {
    externalName,
    externalCompany: form.company,
    externalEmail: form.email,
    externalContacts: form.contacts,
    externalVisitReason: form.visitReason,
    locationName: form.locationName
  };
}

export function scheduleToVisitorPatchRow(
  schedule: BookingScheduleInterface,
  visitor: ScheduleVisitorDisplayEntry
): VisitorBookingTableRow {
  const start = schedule.start ?? schedule.startDate ?? "";
  const end = schedule.end ?? schedule.endDate ?? "";
  const externalName = visitorDisplayName(visitor) || schedule.externalName?.trim() || "";

  return {
    ...schedule,
    start,
    end,
    externalName,
    externalEmail: visitor.email ?? schedule.externalEmail,
    externalCompany: visitor.company ?? schedule.externalCompany,
    externalContacts: visitor.contacts ?? schedule.externalContacts,
    externalVisitReason: visitor.visitReason ?? schedule.externalVisitReason,
    locationName: visitor.locationName ?? schedule.locationName,
    visitorRegistrationId: visitor.visitorRegistrationId ?? schedule.visitorRegistrationId,
    rowId: `schedule-visitor-${schedule.id}-${visitor.visitorRegistrationId ?? visitor.email ?? "0"}`
  };
}

type BuildVisitorInfoPatchResult =
  | { kind: "visit-only"; body: PatchVisitOnlyBookingBody }
  | {
      kind: "conference";
      body: PatchConferenceVisitorRegistrationBody;
      visitorRegistrationId: number;
    }
  | null;

export function buildVisitorInfoPatchPayload({
  schedule,
  visitor,
  draft,
  locations,
  timezone
}: {
  schedule: BookingScheduleInterface;
  visitor: ScheduleVisitorDisplayEntry;
  draft: VisitorBookingEditDraft;
  locations: LocationInventory[] | undefined;
  timezone: string;
}): BuildVisitorInfoPatchResult {
  const row = scheduleToVisitorPatchRow(schedule, visitor);

  if (isConferenceVisitorRegistrationRow(schedule)) {
    const body = buildPatchConferenceVisitorRegistrationPayload({ row, draft, locations });
    const visitorRegistrationId = visitor.visitorRegistrationId ?? schedule.visitorRegistrationId;
    if (!body || visitorRegistrationId == null) {
      return null;
    }
    return { kind: "conference", body, visitorRegistrationId };
  }

  const body = buildPatchVisitOnlyBookingPayload({ row, draft, locations, timezone });
  if (!body) {
    return null;
  }
  return { kind: "visit-only", body };
}
